/* eslint-disable @typescript-eslint/consistent-type-assertions -- Используем для приведения conditional-типов */

import { useCallback, useMemo } from 'react';

import { DistributiveOmit } from '@direct-frontend/stdlib';
import { BaseFormActions, FormInstance } from '../form/types';
import { BaseValidationError } from '../types';

import { ArrayFieldInstance, ArrayFieldInstanceWithoutAsyncActions } from './createArrayField';
import {
    NeverToEmpty,
    ObjectField,
    ObjectFieldInstance,
    ObjectFieldInstanceWithoutAsyncActions,
} from './createObjectField';
import { Primitive, PrimitiveFieldInstance } from './createPrimitiveField';
import { BaseUnionConstituent, BaseUnionFieldActionsMap, InferKeyValue, UnionFieldInstance } from './createUnionField';
import {
    $internals as $fieldInternals,
    BaseField,
    BaseFieldActionsMap,
    BaseFieldAsyncActionsMap,
    BaseFields,
    BaseFieldSelectorsMap,
    BoundFieldActions,
    BoundFieldAsyncActions,
    BoundFieldSelectors,
    FieldActionsMap,
    FieldAsyncActionsMap,
    FieldInstance,
    FieldSelectorsMap,
    InferFieldsValue,
    InferFieldValue,
} from './types';
import { validateField } from './validation';
import { type ScrollFieldProps, getScrollFieldProps } from './scrollToField';
import { FieldState } from '../store/types';

/** Набор полей, доступных при использовании хука `useField` с ЛЮБЫМ полем */
interface UsedAnyField<
    FieldValue,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<FieldValue, never, never>,
    FieldSelectors extends FieldSelectorsMap<FieldValue>,
    FieldAsyncActions,
    FieldName extends string,
> {
    /** Название поля */
    name: FieldName;
    /** Текущее значение поля */
    value: FieldValue;
    /** Текущее состояние поля */
    state: FieldState<FieldValue, FieldError>;
    /** Текущее значение контекста поля */
    context: FieldContext;
    /** Экшены поля */
    actions: NeverToEmpty<BoundFieldActions<FieldActions>>;
    /** Селекторы поля */
    selectors: BoundFieldSelectors<FieldSelectors>;
    /** Асинхронные экшены поля */
    asyncActions: NeverToEmpty<BoundFieldAsyncActions<FieldAsyncActions>>;
    /** Запускает повторную валидацию поля и всех его дочерних полей */
    revalidate: () => void;
    /** Возвращает пропcы для скролла поля */
    getScrollProps: () => ScrollFieldProps;
    /** Скроллит страницу к полю */
    scrollToField: () => void;
}

// 1. Checkbox из Библиотеки Компонентов вместо value принимает isSelected, поэтому переименовываем проп
// 2. Выключаем дистрибутивность, чтобы не превращать юнионы значений в юнионы объектов
type PrimitiveFieldProps<FieldValue> = [FieldValue] extends [boolean]
    ? {
          isSelected: FieldValue;
          onBlur: () => void;
          onChange: (value: FieldValue) => void;
      }
    : {
          value: FieldValue;
          onBlur: () => void;
          onChange: (value: FieldValue) => void;
      };

/** Примитивное поле, возвращаемое из хука `useField` */
export interface UsedPrimitiveField<
    FieldValue extends Primitive,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<FieldValue, never, never>,
    FieldSelectors extends FieldSelectorsMap<FieldValue>,
    FieldAsyncActions,
    FieldName extends string,
> extends UsedAnyField<
        FieldValue,
        FieldError,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions,
        FieldName
    > {
    /** Обновляет текущее значение поля */
    handleChange: (value: FieldValue) => void;
    /** Обработчик события `blur` */
    handleBlur: () => void;
    /** Формирует объект пропсов для компонентов из Библиотеки Компонентов */
    getProps: () => PrimitiveFieldProps<FieldValue>;
}

/** Объектное поле, возвращаемое из хука `useField` */
export interface UsedObjectField<
    InnerFields extends BaseFields,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<InferFieldsValue<InnerFields>, never, FieldContext>,
    FieldSelectors extends FieldSelectorsMap<InferFieldsValue<InnerFields>>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ObjectFieldInstanceWithoutAsyncActions<InnerFields, FieldError, FieldContext, FieldActions, FieldSelectors>
    >,
    FieldName extends string,
> extends UsedAnyField<
            InferFieldsValue<InnerFields>,
            FieldError,
            FieldContext,
            FieldActions,
            FieldSelectors,
            FieldAsyncActions,
            FieldName
        >,
        Pick<
            ObjectFieldInstance<
                InnerFields,
                FieldError,
                FieldContext,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions,
                FieldName
            >,
            'setValue'
        > {
    /** Вложенные поля */
    fields: {
        [FieldName in keyof InnerFields & string]: FieldInstance<InnerFields[FieldName], FieldName>;
    };
}

/** Поле-массив, возвращаемое из хука `useField` */
export interface UsedArrayField<
    InnerField extends BaseField,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<InferFieldValue<InnerField>[], never, FieldContext>,
    FieldSelectors extends FieldSelectorsMap<InferFieldValue<InnerField>[]>,
    FieldAsyncActions extends FieldAsyncActionsMap<
        ArrayFieldInstanceWithoutAsyncActions<InnerField, FieldError, FieldContext, FieldActions, FieldSelectors>
    >,
    FieldName extends string,
> extends UsedAnyField<
            InferFieldValue<InnerField>[],
            FieldError,
            FieldContext,
            FieldActions,
            FieldSelectors,
            FieldAsyncActions,
            FieldName
        >,
        Pick<
            ArrayFieldInstance<
                InnerField,
                FieldError,
                FieldContext,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions,
                FieldName
            >,
            'setValues' | 'addValue' | 'insertValue' | 'replaceValue' | 'removeValue' | 'fieldAt'
        > {
    /** Поля-элементы массива */
    fields: FieldInstance<InnerField, FieldName>[];
}

/** Поле-юнион, возвращаемое из хука `useField` */
export type UsedUnionField<
    Key extends string,
    UnionConstituent extends BaseUnionConstituent<Key>,
    UnionFieldActions extends BaseUnionFieldActionsMap,
    FieldName extends string,
> =
    UnionConstituent extends ObjectField<
        infer InnerFields,
        infer FieldError,
        infer _,
        infer FieldContext,
        infer ObjectFieldActions,
        infer FieldSelectors,
        infer ObjectFieldAsyncActions
    >
        ? DistributiveOmit<
              UsedObjectField<
                  InnerFields,
                  FieldError,
                  FieldContext,
                  ObjectFieldActions,
                  FieldSelectors,
                  ObjectFieldAsyncActions,
                  FieldName
              >,
              'actions' | 'setValue'
          > & {
              /** Текущее значения поля-ключа, которое может быть использовано для сужения типа инстанса поля */
              key: InferKeyValue<Key, InnerFields>;

              /** Действия union-поля */
              actions: BoundFieldActions<
                  // убираем never, если есть общие поля, то возвращаем ошибку
                  NeverToEmpty<ObjectFieldActions> & NeverToEmpty<UnionFieldActions>
              >;
          }
        : never;

/**
 * Возвращает текущее состояние примитивного поля и методы для работы с ним
 * @param field Инстанс примитивного поля
 * @returns Текущее состояние примитивного поля и методы для работы с ним
 **/
export function useField<
    FieldValue extends Primitive,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<FieldValue, never, FieldContext>,
    FieldSelectors extends BaseFieldSelectorsMap,
    FieldAsyncActions extends BaseFieldAsyncActionsMap,
    FieldName extends string,
>(
    field: PrimitiveFieldInstance<
        FieldValue,
        FieldError,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions,
        FieldName
    >
): UsedPrimitiveField<FieldValue, FieldError, FieldContext, FieldActions, FieldSelectors, FieldAsyncActions, FieldName>;

/**
 * Возвращает текущее состояние объектного поля и методы для работы с ним
 * @param field Инстанс объектного поля
 * @returns Текущее состояние объектного поля и методы для работы с ним
 **/
export function useField<
    InnerFields extends BaseFields,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<InferFieldsValue<InnerFields>, never, FieldContext>,
    FieldSelectors extends BaseFieldSelectorsMap,
    FieldAsyncActions extends BaseFieldAsyncActionsMap,
    FieldName extends string,
>(
    field: ObjectFieldInstance<
        InnerFields,
        FieldError,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions,
        FieldName
    >
): UsedObjectField<InnerFields, FieldError, FieldContext, FieldActions, FieldSelectors, FieldAsyncActions, FieldName>;

/**
 * Возвращает текущее состояние поля-массива и методы для работы с ним
 * @param field Инстанс поля-массива
 * @returns Текущее состояние поля-массива и методы для работы с ним
 **/
export function useField<
    InnerField extends BaseField,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends FieldActionsMap<InferFieldValue<InnerField>[], never, FieldContext>,
    FieldSelectors extends BaseFieldSelectorsMap,
    FieldAsyncActions extends BaseFieldAsyncActionsMap,
    FieldName extends string,
>(
    field: ArrayFieldInstance<
        InnerField,
        FieldError,
        FieldContext,
        FieldActions,
        FieldSelectors,
        FieldAsyncActions,
        FieldName
    >
): UsedArrayField<InnerField, FieldError, FieldContext, FieldActions, FieldSelectors, FieldAsyncActions, FieldName>;

/**
 * Возвращает текущее состояние поля-юниона и методы для работы с ним
 * @param field Инстанс поля-юниона
 * @returns Текущее состояние поля-юниона и методы для работы с ним
 **/
export function useField<
    Key extends string,
    UnionConstituent extends BaseUnionConstituent<Key>,
    UnionFieldActions extends BaseUnionFieldActionsMap,
    FieldName extends string,
>(
    field: UnionFieldInstance<Key, UnionConstituent, UnionFieldActions, FieldName>
): UsedUnionField<Key, UnionConstituent, UnionFieldActions, FieldName>;

export function useField<
    PrimitiveFieldValue extends Primitive,
    ObjectInnerFields extends BaseFields,
    ArrayInnerField extends BaseField,
    UnionKey extends string,
    UnionConstituent extends BaseUnionConstituent<UnionKey>,
    UnionFieldActions extends BaseUnionFieldActionsMap,
    FieldError extends BaseValidationError,
    FieldContext,
    FieldActions extends BaseFieldActionsMap,
    FieldSelectors extends BaseFieldSelectorsMap,
    FieldAsyncActions extends BaseFieldAsyncActionsMap,
    FieldName extends string,
>(
    field:
        | PrimitiveFieldInstance<
              PrimitiveFieldValue,
              FieldError,
              FieldContext,
              FieldActions,
              FieldSelectors,
              FieldAsyncActions,
              FieldName
          >
        | ObjectFieldInstance<
              ObjectInnerFields,
              FieldError,
              FieldContext,
              FieldActions,
              FieldSelectors,
              FieldAsyncActions,
              FieldName
          >
        | ArrayFieldInstance<
              ArrayInnerField,
              FieldError,
              FieldContext,
              FieldActions,
              FieldSelectors,
              FieldAsyncActions,
              FieldName
          >
        | UnionFieldInstance<UnionKey, UnionConstituent, UnionFieldActions, FieldName>
):
    | UsedPrimitiveField<
          PrimitiveFieldValue,
          FieldError,
          FieldContext,
          FieldActions,
          FieldSelectors,
          FieldAsyncActions,
          FieldName
      >
    | UsedObjectField<
          ObjectInnerFields,
          FieldError,
          FieldContext,
          FieldActions,
          FieldSelectors,
          FieldAsyncActions,
          FieldName
      >
    | UsedArrayField<
          ArrayInnerField,
          FieldError,
          FieldContext,
          FieldActions,
          FieldSelectors,
          FieldAsyncActions,
          FieldName
      >
    | UsedUnionField<UnionKey, UnionConstituent, UnionFieldActions, FieldName> {
    const fieldValue = field[$fieldInternals].useValue();
    const fieldState = field[$fieldInternals].useState();
    const fieldContext = field[$fieldInternals].useContext();

    // eslint-disable-next-line react-hooks/exhaustive-deps -- id поля достаточно, так как в поле нет своего состояния
    const revalidate = useMemo(() => field.revalidate.bind(field), [field.id]);
    const handleBlur = useCallback(() => {
        field[$fieldInternals].touch();
        if (field.type === 'primitive') {
            validateField({
                cause: 'blur',
                form: field.form as FormInstance<
                    string,
                    BaseFields,
                    BaseValidationError,
                    unknown,
                    unknown,
                    BaseFormActions,
                    unknown
                >,
                field: field[$fieldInternals].declaration,
                fieldId: field.id,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- id поля достаточно, так как в поле нет своего состояния
    }, [field.id]);

    const innerFieldsOrder = 'order' in fieldState ? fieldState.order : null;
    const innerFields = useMemo(() => {
        // Искусственно добавляем `order` в зависимости useMemo, так как от него зависит результат вызова fieldAt
        if (field.type === 'array' && innerFieldsOrder) {
            const fixedFieldValue = fieldValue as InferFieldValue<ArrayInnerField>[];
            return fixedFieldValue.map((_, index) => field.fieldAt(index));
        }

        return;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- id поля достаточно, так как в поле нет своего состояния
    }, [fieldValue, field.id, innerFieldsOrder]);

    return useMemo(() => {
        // Если в объектном поле есть key, то поле – юнион
        if ('key' in field && field.type === 'object') {
            return {
                name: field.name,
                value: fieldValue,
                key: field.key,
                state: fieldState,
                context: fieldContext,
                fields: field.fields,
                actions: field.actions,
                selectors: field.selectors,
                asyncActions: field.asyncActions,
                revalidate,
                getScrollProps: () =>
                    getScrollFieldProps({ formName: field.form.name, formId: field.form.id, fieldId: field.id }),
                scrollToField: field[$fieldInternals].scrollToField,
            } as unknown as UsedUnionField<UnionKey, UnionConstituent, UnionFieldActions, FieldName>;
        }

        if (field.type === 'object') {
            return {
                name: field.name,
                value: fieldValue as InferFieldsValue<ObjectInnerFields>,
                state: fieldState as FieldState<InferFieldsValue<ObjectInnerFields>, FieldError>,
                context: fieldContext,
                getScrollProps: () =>
                    getScrollFieldProps({ formName: field.form.name, formId: field.form.id, fieldId: field.id }),
                fields: field.fields,
                setValue: field.setValue,
                actions: field.actions,
                selectors: field.selectors,
                asyncActions: field.asyncActions,
                revalidate,
                scrollToField: field[$fieldInternals].scrollToField,
            } satisfies UsedObjectField<
                ObjectInnerFields,
                FieldError,
                FieldContext,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions,
                FieldName
            >;
        }

        if (field.type === 'array') {
            return {
                actions: field.actions,
                selectors: field.selectors,
                asyncActions: field.asyncActions,
                name: field.name,
                getScrollProps: () =>
                    getScrollFieldProps({ formName: field.form.name, formId: field.form.id, fieldId: field.id }),
                value: fieldValue as InferFieldValue<ArrayInnerField>[],
                state: fieldState as FieldState<InferFieldValue<ArrayInnerField>[], FieldError>,
                context: fieldContext,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- для массивов значение точно есть
                fields: innerFields!,
                addValue: field.addValue,
                insertValue: field.insertValue,
                removeValue: field.removeValue,
                fieldAt: field.fieldAt,
                replaceValue: field.replaceValue,
                setValues: field.setValues,
                revalidate,
                scrollToField: field[$fieldInternals].scrollToField,
            } satisfies UsedArrayField<
                ArrayInnerField,
                FieldError,
                FieldContext,
                FieldActions,
                FieldSelectors,
                FieldAsyncActions,
                FieldName
            >;
        }

        return {
            actions: field.actions,
            selectors: field.selectors,
            asyncActions: field.asyncActions,
            name: field.name,
            value: fieldValue as PrimitiveFieldValue,
            state: fieldState as FieldState<PrimitiveFieldValue, FieldError>,
            context: fieldContext,
            handleBlur,
            handleChange: field.setValue,
            revalidate,
            getScrollProps: () =>
                getScrollFieldProps({ formName: field.form.name, formId: field.form.id, fieldId: field.id }),
            scrollToField: field[$fieldInternals].scrollToField,
            getProps: () => {
                if (typeof fieldValue === 'boolean') {
                    return {
                        isSelected: fieldValue,
                        onBlur: handleBlur,
                        onChange: field.setValue,
                    } as PrimitiveFieldProps<PrimitiveFieldValue>;
                }

                return {
                    value: fieldValue,
                    onBlur: handleBlur,
                    onChange: field.setValue,
                } as PrimitiveFieldProps<PrimitiveFieldValue>;
            },
        } satisfies UsedPrimitiveField<
            PrimitiveFieldValue,
            FieldError,
            FieldContext,
            FieldActions,
            FieldSelectors,
            FieldAsyncActions,
            FieldName
        >;
    }, [field, fieldState, fieldContext, fieldValue, handleBlur, innerFields, revalidate]);
}
